import { useEffect } from 'react';
import { safeLocalStorage } from '@toss/storage';
import { type SupportedLocale } from '@zep-us/i18n';
import { useEffectOnce } from '@/hooks';
import { type TemplateAiTutor } from '@/shared/components/Chatbot';
import { useQuizTitle } from '../features/build-quiz-title';
import { type BuildSchemaOutputType, useBuildFormContext } from '../form/schema';
import { useSetPersistButtonVisible } from '../hook/useConfirmPersistData';
import { usePersistBuildForm } from '../hook/usePersistBuildForm';

/**
 *
 * @param enabled - 빌드 form의 변경을 감지하여, 로컬스토리지에 임시 저장 기능을 구현하는 컴포넌트
 * @returns null
 */
export const BuildFormSubscription = ({
  enabled
}: {
  enabled: boolean;
}) => {
  useBuildQuizsetFormSubscription({
    enableSubscription: enabled
  });
  return null;
};
const useBuildQuizsetFormSubscription = ({
  enableSubscription
}: {
  enableSubscription: boolean;
}) => {
  const {
    updatePersistData
  } = usePersistBuildForm();
  const {
    watch
  } = useBuildFormContext();
  const setIsPersistButtonVisible = useSetPersistButtonVisible();
  const {
    titlePlaceholder
  } = useQuizTitle();

  // 빌드 페이지 개선하며 기존 build 스토리지 삭제
  useEffectOnce(() => {
    safeLocalStorage.remove('quiz_build_v2');
    safeLocalStorage.remove('quiz_build');
    safeLocalStorage.remove('quiz_build_v3');
  });

  // 빌드 폼 변경시 저장 데이터 업데이트
  useEffect(() => {
    if (!enableSubscription) return;
    const subscription = watch(value => {
      // 폼 변경 시에는 이어서하기 저장 버튼 숨김
      setIsPersistButtonVisible(false);
      updatePersistData({
        ...value,
        title: value.title || titlePlaceholder,
        quizList: value.quizList as BuildSchemaOutputType['quizList'],
        materials: value.materials as BuildSchemaOutputType['materials'],
        aiTranslationLanguages: value.aiTranslationLanguages as SupportedLocale[],
        aiTutor: value.aiTutor as Pick<TemplateAiTutor, 'id' | 'name' | 'introduction'> & {
          avatarSrc: string;
        }
      });
    });
    return () => subscription.unsubscribe();
  }, [watch, updatePersistData, setIsPersistButtonVisible, enableSubscription, titlePlaceholder]);
  return null;
};